// canvas/text.js: canvas font helper.
//
// canvasFont(sizePx) is the one place that decides the canvas font
// assignments across the renderer. Every ctx.font = ... call should
// route through here so:
//   1. Nunito is used everywhere or nowhere, no mixed faces.
//   2. The system-font accessibility override (synthesis §4.3) toggles
//      ALL canvas text in one place.
//   3. Font sizes are quantized in one helper (see snapPixelSize).
//
// `system` flag follows settings.system_font. When true: returns a
// sans-serif stack instead. Caller passes this through; we don't read
// settings here so the function stays pure and tests don't need DOM.

/**
 * Quantize a desired px size to a 4-multiple.
 *
 * Originally a crispness rule for the m6x11plus pixel font; with the vector
 * Nunito face it's no longer required, but it's retained as harmless size
 * quantization so the renderer draws from a small, consistent family of
 * sizes (12/16/20/24/28/32/36/40/48) rather than arbitrary fractional px.
 *
 * @param {number} px
 * @returns {number}
 */
export function snapPixelSize(px) {
  if (typeof px !== 'number' || !Number.isFinite(px) || px <= 0) return 16;
  const snapped = Math.round(px / 4) * 4;
  return Math.max(8, snapped);
}

// Module-level system-font toggle. Set once at app startup from
// loadSettings().system_font (see App.jsx / main.jsx). Canvas renderers
// don't need to thread the flag, they call canvasFont(size) and the
// helper picks the right stack.
let _systemMode = false;

/**
 * Set the canvas-wide system-font mode. App startup calls this once;
 * settings panel calls it on toggle so the next redraw picks up the
 * change. No event/listener, canvas redraws are driven by useGameLoop.
 *
 * @param {boolean} enabled
 */
export function setCanvasSystemFont(enabled) {
  _systemMode = !!enabled;
}

export function getCanvasSystemFont() {
  return _systemMode;
}

/**
 * Build a `ctx.font` value for canvas use.
 *
 * @param {number} sizePx
 * @param {{ system?: boolean, weight?: number|string }} [opts]
 * @returns {string}
 */
export function canvasFont(sizePx, opts = {}) {
  const size = snapPixelSize(sizePx);
  const weight = opts.weight ?? 'normal';
  const system = opts.system !== undefined ? opts.system : _systemMode;
  if (system) {
    // Sans-serif fallback for accessibility opt-out (settings.system_font).
    return `${weight} ${size}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  }
  return `${weight} ${size}px 'Nunito', ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
}

/**
 * Pick the largest snapped font size (≤ basePx) at which `text` fits within
 * `maxWidth`, stepping down by 4-px snap increments. #design-a11y: canvas
 * labels (seat nameplates, the trump "No trump" badge) were drawn at a fixed
 * size and clipped when the box was narrow ("Seat 2" → "Sea", "No trump" →
 * truncated). Routing those draws through this helper shrinks the glyphs to
 * fit instead of letting them clip.
 *
 * Pure: the caller supplies `measure(fontPx) → widthPx` (closes over the
 * real ctx.measureText in production; a fake in tests). Never returns below
 * `minPx`, so a hopeless box clamps at a readable floor rather than 0.
 *
 * @param {string} text
 * @param {number} maxWidth   available width in px
 * @param {(fontPx:number)=>number} measure  width of `text` at fontPx
 * @param {{ basePx?: number, minPx?: number }} [opts]
 * @returns {number} a snapped font size in px
 */
export function fitFontSize(text, maxWidth, measure, opts = {}) {
  const basePx = snapPixelSize(opts.basePx ?? 14);
  const minPx = snapPixelSize(opts.minPx ?? 8);
  // Bad inputs → just use the snapped base; nothing to fit against.
  if (typeof measure !== 'function') return basePx;
  if (!text || typeof maxWidth !== 'number' || !Number.isFinite(maxWidth) || maxWidth <= 0) {
    return basePx;
  }
  let size = basePx;
  while (size > minPx) {
    if (measure(size) <= maxWidth) return size;
    size = snapPixelSize(size - 4);
    // snapPixelSize floors at 8 and 4→4-step can stall; guard the loop.
    if (size >= basePx) break;
  }
  return Math.max(minPx, size);
}

/**
 * Shorten `label` with "…" until `measure(label) <= maxWidth`. A trailing
 * " · bot" mark is kept whole: the name gives way, never the mark.
 *
 * @param {string} label
 * @param {number} maxWidth
 * @param {(text: string) => number} measure  width at the font in use
 * @param {string} [keepSuffix]
 * @returns {string}
 */
export function truncateLabelToFit(label, maxWidth, measure, keepSuffix = ' · bot') {
  if (!label || measure(label) <= maxWidth) return label;
  const suffix = label.endsWith(keepSuffix) ? keepSuffix : '';
  const name = suffix ? label.slice(0, -suffix.length) : label;
  for (let n = name.length - 1; n >= 1; n -= 1) {
    const candidate = `${name.slice(0, n).trimEnd()}…${suffix}`;
    if (measure(candidate) <= maxWidth) return candidate;
  }
  return `…${suffix}`;
}

export const _internals = { snapPixelSize };
